import React from 'react'
import { useTranslation } from 'react-i18next'
import Option from '../../components/Option'
import BorderRow from './BorderRow'
import {
  BorderWidthType,
  BorderStyleType,
  BorderColorType,
  BaseEntityInterface,
  BorderType,
} from '@email-editor/ui'
import { UpdateProps } from '../../hooks/useUpdaters'

export const borderStyles = {
  none: 'None',
  dotted: 'Dotted',
  dashed: 'Dashed',
  solid: 'Solid',
}

function allEqual(arr: any[]) {
  return arr.every(v => v === arr[0])
}

export type BorderKey = keyof BorderWidthType | keyof BorderStyleType | keyof BorderColorType

export type BorderUpdater = (
  prop: 'borderStyle' | 'borderWidth' | 'borderColor',
) => (key: BorderKey) => (value: string | number) => void

export type BorderBatchUpdater = (
  prop: 'borderStyle' | 'borderWidth' | 'borderColor',
) => (
  key1: BorderKey,
  key2: BorderKey,
  key3: BorderKey,
  key4: BorderKey,
) => (value: string | number) => void

interface BorderProps<T extends BaseEntityInterface & BorderType> extends BorderType {
  update: BorderUpdater
  batchUpdate: BorderBatchUpdater
  updateProps: UpdateProps<T>
}

function Border<T extends BaseEntityInterface & BorderType>({
  borderWidth,
  borderColor,
  borderStyle,
  update,
  batchUpdate,
  updateProps,
}: BorderProps<T>) {
  const { t } = useTranslation()
  const allPropsEqual =
    allEqual(Object.values(borderWidth)) &&
    allEqual(Object.values(borderColor)) &&
    allEqual(Object.values(borderStyle))

  const handleToggleEqual = () => {
    updateProps(props => ({
      ...props,
      borderColor: {
        borderTopColor: borderColor.borderTopColor,
        borderRightColor: borderColor.borderTopColor,
        borderBottomColor: borderColor.borderTopColor,
        borderLeftColor: borderColor.borderTopColor,
      },
      borderStyle: {
        borderTopStyle: borderStyle.borderTopStyle,
        borderRightStyle: borderStyle.borderTopStyle,
        borderBottomStyle: borderStyle.borderTopStyle,
        borderLeftStyle: borderStyle.borderTopStyle,
      },
      borderWidth: {
        borderTopWidth: borderWidth.borderTopWidth,
        borderRightWidth: borderWidth.borderTopWidth,
        borderBottomWidth: borderWidth.borderTopWidth,
        borderLeftWidth: borderWidth.borderTopWidth,
      },
    }))
  }

  return (
    <Option
      label="settings_options.border.label"
      more
      moreChecked={!allPropsEqual}
      onToggle={handleToggleEqual}
    >
      {checked =>
        checked ? (
          <>
            <div>{t('settings_options.border_top.label')}</div>
            <BorderRow
              color={borderColor.borderTopColor}
              style={borderStyle.borderTopStyle}
              width={borderWidth.borderTopWidth}
              updateWidth={update('borderWidth')('borderTopWidth')}
              updateStyle={update('borderStyle')('borderTopStyle')}
              updateColor={update('borderColor')('borderTopColor')}
            />
            <div>{t('settings_options.border_right.label')}</div>
            <BorderRow
              color={borderColor.borderRightColor}
              style={borderStyle.borderRightStyle}
              width={borderWidth.borderRightWidth}
              updateWidth={update('borderWidth')('borderRightWidth')}
              updateStyle={update('borderStyle')('borderRightStyle')}
              updateColor={update('borderColor')('borderRightColor')}
            />
            <div>{t('settings_options.border_bottom.label')}</div>
            <BorderRow
              color={borderColor.borderBottomColor}
              style={borderStyle.borderBottomStyle}
              width={borderWidth.borderBottomWidth}
              updateWidth={update('borderWidth')('borderBottomWidth')}
              updateStyle={update('borderStyle')('borderBottomStyle')}
              updateColor={update('borderColor')('borderBottomColor')}
            />
            <div>{t('settings_options.border_left.label')}</div>
            <BorderRow
              color={borderColor.borderLeftColor}
              style={borderStyle.borderLeftStyle}
              width={borderWidth.borderLeftWidth}
              updateWidth={update('borderWidth')('borderLeftWidth')}
              updateStyle={update('borderStyle')('borderLeftStyle')}
              updateColor={update('borderColor')('borderLeftColor')}
            />
          </>
        ) : (
          <BorderRow
            color={borderColor.borderTopColor}
            style={borderStyle.borderTopStyle}
            width={borderWidth.borderTopWidth}
            updateWidth={batchUpdate('borderWidth')(
              'borderTopWidth',
              'borderRightWidth',
              'borderBottomWidth',
              'borderLeftWidth',
            )}
            updateStyle={batchUpdate('borderStyle')(
              'borderTopStyle',
              'borderRightStyle',
              'borderBottomStyle',
              'borderLeftStyle',
            )}
            updateColor={batchUpdate('borderColor')(
              'borderTopColor',
              'borderRightColor',
              'borderBottomColor',
              'borderLeftColor',
            )}
          />
        )
      }
    </Option>
  )
}

export default Border
